import { PortfolioItem, PortfolioItemColumn } from 'portfolio/PortfolioItem';
import {
    MyPortfolioCapsulesDataStateAggregation,
    MyPortfolioCapsulesSymbolDataAccumulatedColumn,
    MyPortfolioCapsulesSymbolDataState,
    MyPortfolioCapsulesSymbolDataWeightField,
    MyPortfolioCapsulesSymbolDataWeights,
} from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesDataState';
import { percentAdvTopic } from 'widgets/myPortfolioCapsules/topics/percentAdvTopic';
import { rawCostTopic } from 'widgets/myPortfolioCapsules/topics/costs/rawCostTopic';
import { TopicType } from 'widgets/myPortfolioCapsules/topics/TopicType';
import { MyPortfolioCapsulesWidgetConfigState } from 'widgets/myPortfolioCapsules/store/config/MyPortfolioCapsulesWidgetConfigState';
import { MyPortfolioCapsulesDataItem } from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesData';
import { ReactNode } from 'react';
import { valueTopic } from 'widgets/myPortfolioCapsules/topics/value/valueTopic';
import { IndigoAppContextType } from 'app/AppContext';
import { adjustedCostTopic } from 'widgets/myPortfolioCapsules/topics/costs/adjustedCostTopic';
import { moversTopic } from 'widgets/myPortfolioCapsules/topics/movers/moversTopic';

export interface TopicOptions {
    absolute?: boolean;
}

export interface Topic {
    type: TopicType;
    description: string;
    requiredPortfolioColumns: PortfolioItemColumn[];
    accumulatedColumns: MyPortfolioCapsulesSymbolDataAccumulatedColumn[];
    weights: MyPortfolioCapsulesSymbolDataWeightField[];
    calculateDerivedValues?: (
        item: PortfolioItem,
        symbolDataState: MyPortfolioCapsulesSymbolDataState,
        itemWeights: MyPortfolioCapsulesSymbolDataWeights,
        factor: 1 | -1
    ) => void;
    getSymbolValues(
        aggregatedData: MyPortfolioCapsulesDataStateAggregation,
        config: MyPortfolioCapsulesWidgetConfigState,
        exchangeRate: number
    ): MyPortfolioCapsulesDataItem[];
    options?: TopicOptions;
    help: ReactNode;
    toolbarItems?: JSX.Element;
    getDescription?(
        appContext: IndigoAppContextType,
        config: MyPortfolioCapsulesWidgetConfigState
    ): string;
    featureToggle?: string;

    deriveShouldExcludeZeros(
        config: MyPortfolioCapsulesWidgetConfigState
    ): boolean;
}

export interface TopicWithConfig<TTopicConfig> extends Topic {
    getSymbolValues(
        aggregatedData: MyPortfolioCapsulesDataStateAggregation,
        config: MyPortfolioCapsulesWidgetConfigState & TTopicConfig,
        exchangeRate: number
    ): MyPortfolioCapsulesDataItem[];
    getInitialTopicConfig: () => TTopicConfig;
    getDescription?(
        appContext: IndigoAppContextType,
        config: MyPortfolioCapsulesWidgetConfigState & TTopicConfig
    ): string;
    formatValue?(
        appContext: IndigoAppContextType,
        value: number,
        config: MyPortfolioCapsulesWidgetConfigState & TTopicConfig
    ): string;
}

const topicDictionary: Record<TopicType, Topic> = {
    [TopicType.PercentAdv]: percentAdvTopic,
    [TopicType.AdjustedCost]: adjustedCostTopic,
    [TopicType.Movers]: moversTopic,
    [TopicType.RawCost]: rawCostTopic,
    [TopicType.Value]: valueTopic,
};

function nullTopic(type: TopicType): Topic {
    return {
        type,
        description: `${type}`,
        requiredPortfolioColumns: [],
        accumulatedColumns: [],
        getSymbolValues: () => [],
        help: undefined,
        weights: [],
        deriveShouldExcludeZeros: () => false,
    };
}

export const isValidTopic = (topicType: TopicType) =>
    !!topicDictionary[topicType];

export const getTopic = (topicType: TopicType) =>
    topicDictionary[topicType] ?? nullTopic(topicType);

export const topics: Topic[] = Object.values(topicDictionary);
